import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search } from 'lucide-react';
import { listLucideIconNames } from '../../utils/placemarkIcons/lucideIconCatalog';
import { getPlacemarkGlyphCanvas, resolveIconName, } from '../../utils/placemarkIcons/placemarkIconRenderer';
import { ICON_CATEGORIES, getCategoryForIcon, humanizeIconName, } from '../../utils/placemarkIcons/iconCategories';
const PICKER_WIDTH = 360;
const PICKER_HEIGHT = 420;
const TILE_SIZE = 44;
const IconTile = ({ iconName, color, isSelected, onClick }) => {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const glyph = getPlacemarkGlyphCanvas(iconName, color);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(glyph, 0, 0, canvas.width, canvas.height);
    }, [iconName, color]);
    return (_jsx("button", { onClick: onClick, title: humanizeIconName(iconName), className: "flex items-center justify-center rounded transition-colors", style: {
            width: TILE_SIZE,
            height: TILE_SIZE,
            backgroundColor: isSelected ? 'var(--forest)' : 'transparent',
            border: isSelected ? '1px solid var(--fern)' : '1px solid transparent',
        }, onMouseEnter: (e) => {
            if (!isSelected)
                e.currentTarget.style.backgroundColor = 'var(--pewter)';
        }, onMouseLeave: (e) => {
            if (!isSelected)
                e.currentTarget.style.backgroundColor = 'transparent';
        }, children: _jsx("canvas", { ref: canvasRef, width: 32, height: 32, style: { width: 32, height: 32 } }) }));
};
export const IconPicker = ({ selectedIcon, selectedColor, onIconChange, onClose, position, }) => {
    const popoverRef = useRef(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [category, setCategory] = useState('All');
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 120);
        return () => clearTimeout(handle);
    }, [search]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);
    const allIconNames = useMemo(() => listLucideIconNames(), []);
    const filteredIcons = useMemo(() => {
        return allIconNames.filter((name) => {
            if (debouncedSearch && !name.toLowerCase().includes(debouncedSearch))
                return false;
            if (category !== 'All' && getCategoryForIcon(name) !== category)
                return false;
            return true;
        });
    }, [allIconNames, debouncedSearch, category]);
    const resolvedSelection = resolveIconName(selectedIcon);
    // Constrain to viewport.
    const clampedX = Math.min(position.x, window.innerWidth - PICKER_WIDTH - 8);
    const clampedY = Math.min(position.y, window.innerHeight - PICKER_HEIGHT - 8);
    return (_jsxs("div", { ref: popoverRef, className: "fixed z-[70] rounded-lg shadow-xl flex flex-col", style: {
            left: Math.max(8, clampedX),
            top: Math.max(8, clampedY),
            width: PICKER_WIDTH,
            height: PICKER_HEIGHT,
            backgroundColor: 'var(--charcoal)',
            border: '1px solid var(--pewter)',
        }, children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2 rounded-t-lg", style: { backgroundColor: 'var(--pewter)' }, children: [_jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: "Choose Icon" }), _jsx("button", { onClick: onClose, className: "p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 transition-colors", style: { color: 'var(--dew)' }, children: _jsx(X, { size: 16 }) })] }), _jsx("div", { className: "px-3 py-2", style: { borderBottom: '1px solid var(--pewter)' }, children: _jsxs("div", { className: "relative", children: [_jsx(Search, { size: 14, style: {
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--dew)',
                                opacity: 0.6,
                            } }), _jsx("input", { type: "text", value: search, onChange: (e) => setSearch(e.target.value), placeholder: "Search icons...", className: "w-full pl-7 pr-2 py-1.5 rounded text-sm", style: {
                                backgroundColor: 'var(--pewter)',
                                color: 'var(--sand)',
                                border: '1px solid var(--charcoal)',
                            } })] }) }), _jsx("div", { className: "flex gap-1 px-3 py-2 overflow-x-auto", style: { borderBottom: '1px solid var(--pewter)' }, children: ['All', ...ICON_CATEGORIES].map((cat) => (_jsx("button", { onClick: () => setCategory(cat), className: "px-2 py-0.5 rounded text-xs whitespace-nowrap transition-colors", style: {
                        backgroundColor: category === cat ? 'var(--forest)' : 'var(--pewter)',
                        color: category === cat ? 'var(--sand)' : 'var(--dew)',
                    }, children: cat }, cat))) }), _jsx("div", { className: "flex-1 overflow-y-auto p-2", children: filteredIcons.length === 0 ? (_jsx("div", { className: "flex items-center justify-center h-full text-sm", style: { color: 'var(--pewter)' }, children: "No icons match" })) : (_jsx("div", { className: "grid grid-cols-6 gap-1", children: filteredIcons.map((name) => (_jsx(IconTile, { iconName: name, color: selectedColor, isSelected: name === resolvedSelection, onClick: () => onIconChange(name) }, name))) })) })] }));
};
